import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Skeleton,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import refreshAccessToken from '@core/actions/accessToken/refreshAccessToken.action';
import { getInitialSpaceId } from '@core/selectors/space/space.selectors';
import {
  fetchHomeUpcomingAssignments,
  HomeUpcomingAssignment,
} from '@core/assignments/api/homeUpcomingAssignments';
import { getHomeViewRole } from '@app/selectors/home';
import { HOME_VIEW_ROLE_LABELS } from '@app/constants/home';
import UpcomingAssignmentRow from './UpcomingAssignmentRow';
import UpcomingAssignmentsModal from './UpcomingAssignmentsModal';

const WIDGET_LIMIT = 5;
const FETCH_LIMIT = 50;

const STRINGS = {
  title: 'Upcoming assignments',
  empty: 'No upcoming assignments',
  viewAll: 'View all upcoming assignments',
};

const AssignmentRowSkeleton = () => (
  <Box display="flex" flexDirection="column" gap={1} p={2}>
    <Skeleton kind="text" width="70%" height={24} />
    <Skeleton kind="text" width="40%" height={18} />
  </Box>
);

const UpcomingAssignmentsWidget = () => {
  const { palette } = useTheme();
  const dispatch = useDispatch<any>();
  const store = useStore<any>();
  const spaceId = useSelector((state: any) => getInitialSpaceId(state));
  const viewRole = useSelector(getHomeViewRole);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allItems, setAllItems] = useState<HomeUpcomingAssignment[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!spaceId) {
          if (isMounted) {
            setAllItems([]);
          }
          return;
        }

        const accessToken = await refreshAccessToken(
          dispatch,
          store.getState
        )({ spaceId });

        const { data } = await fetchHomeUpcomingAssignments({
          accessToken,
          limit: FETCH_LIMIT,
          page: 0,
          includeSubmitted: true,
        });

        if (isMounted) {
          setAllItems(data.upcomingAssignments || []);
        }
      } catch (e: any) {
        if (isMounted) {
          setError('Failed to load upcoming assignments');
          setAllItems([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [dispatch, spaceId, store]);

  const filteredItems = useMemo(
    () => allItems.filter((assignment) => assignment.role === viewRole),
    [allItems, viewRole]
  );

  const widgetItems = useMemo(
    () => filteredItems.slice(0, WIDGET_LIMIT),
    [filteredItems]
  );

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <>
          <AssignmentRowSkeleton />
          <AssignmentRowSkeleton />
          <AssignmentRowSkeleton />
        </>
      );
    }

    if (error) {
      return (
        <Box p={2}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!widgetItems.length) {
      return (
        <Box p={2}>
          <Typography variant="body2">
            {STRINGS.empty} in {HOME_VIEW_ROLE_LABELS[viewRole].toLowerCase()}{' '}
            view.
          </Typography>
        </Box>
      );
    }

    return widgetItems.map((assignment) => (
      <UpcomingAssignmentRow
        key={assignment.id}
        assignment={assignment}
        viewRole={viewRole}
        variant="widget"
      />
    ));
  }, [error, isLoading, viewRole, widgetItems]);

  return (
    <>
      <Card
        sx={{
          borderRadius: '0.75rem',
          padding: '1rem',
          boxShadow: 'none',
          border: `1px solid ${palette.divider}`,
          bgcolor: palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '600px',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
          mt={-0.5}
        >
          <Typography variant="subtitle1" fontWeight="medium" color="primary">
            {STRINGS.title}
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap={1} flex={1}>
          {content}
        </Box>

        <Button
          fullWidth
          kind="secondary"
          endIcon={<ArrowForwardIcon />}
          onClick={() => setIsModalOpen(true)}
          disabled={isLoading || !!error}
        >
          {STRINGS.viewAll}
        </Button>
      </Card>

      <UpcomingAssignmentsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        assignments={filteredItems}
        viewRole={viewRole}
      />
    </>
  );
};

export default UpcomingAssignmentsWidget;
